import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Hero.css';
import card1 from '../../images/cards/CYBERLINGO-Advanced-13.jpg';
import card2 from '../../images/cards/CYBERLINGO-Advanced-27.jpg';
import card3 from '../../images/cards/CYBERLINGO-Advanced-30.jpg';
import card4 from '../../images/cards/CYBERLINGO-Easy-13.jpg';
import card5 from '../../images/cards/CYBERLINGO-Easy-3.jpg';

const Hero = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [shuffleIndex, setShuffleIndex] = useState(0);
  // Remove isPaused state or keep it but don't use it on the container

  // Card configurations for different shuffle positions
  const shuffleConfigurations = [
    // Configuration 1 - Original spread
    [
      { rotation: -15, translateX: -150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -5, translateX: -70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 0, translateX: 0, translateY: 0, translateZ: 0, scale: 1.05 },
      { rotation: 5, translateX: 70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 15, translateX: 150, translateY: 10, translateZ: -60, scale: 0.9 }
    ],
    // Configuration 2 - Shifted left
    [
      { rotation: 15, translateX: 150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -15, translateX: -150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -5, translateX: -70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 0, translateX: 0, translateY: 0, translateZ: 0, scale: 1.05 },
      { rotation: 5, translateX: 70, translateY: 0, translateZ: -20, scale: 0.95 }
    ],
    // Configuration 3 - Center focus
    [
      { rotation: 5, translateX: 70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 15, translateX: 150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -15, translateX: -150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -5, translateX: -70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 0, translateX: 0, translateY: 0, translateZ: 0, scale: 1.05 }
    ],
    // Configuration 4 - Shifted right
    [
      { rotation: -5, translateX: -70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 0, translateX: 0, translateY: 0, translateZ: 0, scale: 1.05 },
      { rotation: 5, translateX: 70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 15, translateX: 150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -15, translateX: -150, translateY: 10, translateZ: -60, scale: 0.9 }
    ],
    // Configuration 5 - Fan out
    [
      { rotation: 0, translateX: 0, translateY: 0, translateZ: 0, scale: 1.05 },
      { rotation: 5, translateX: 70, translateY: 0, translateZ: -20, scale: 0.95 },
      { rotation: 15, translateX: 150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -15, translateX: -150, translateY: 10, translateZ: -60, scale: 0.9 },
      { rotation: -5, translateX: -70, translateY: 0, translateZ: -20, scale: 0.95 }
    ]
  ];

  const cards = [
    {
      id: 1,
      image: card1 
    },
    {
      id: 2,
      image: card4 
    },
    {
      id: 3,
      image: card2 
    },
    {
      id: 4,
      image: card5 
    },
    {
      id: 5,
      image: card3
    }
  ];

  // Auto-shuffle effect - Always running
  useEffect(() => {
    const interval = setInterval(() => {
      setShuffleIndex((prevIndex) => (prevIndex + 1) % shuffleConfigurations.length);
    }, 3000); // Change configuration every 3 seconds

    return () => clearInterval(interval);
  }, [shuffleConfigurations.length]);

  const currentConfig = shuffleConfigurations[shuffleIndex];

  return (
    <section className="hero">
      <div className="container">
        <motion.h1 
          className="hero__title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          CyberLingo — Where Cybersecurity Meets Fun
        </motion.h1>
        
        <div 
          className="hero__cards" 
          ref={ref}
          // Remove onMouseEnter and onMouseLeave from here
        >
          <AnimatePresence mode="sync">
            {cards.map((card, index) => {
              const config = currentConfig[index];
              const isCenter = config.scale === 1.05;
              
              return (
                <motion.div
                  key={`${card.id}-${shuffleIndex}`}
                  className={`hero__card ${isCenter ? 'hero__card--center' : ''}`}
                  initial={{
                    opacity: 0,
                    rotateY: config.rotation - 180,
                    x: config.translateX,
                    y: config.translateY + 50,
                    z: config.translateZ,
                    scale: 0.5,
                  }}
                  animate={{
                    opacity: 1,
                    rotateY: config.rotation,
                    x: config.translateX,
                    y: config.translateY,
                    z: config.translateZ,
                    scale: config.scale,
                  }}
                  exit={{
                    opacity: 0,
                    rotateY: config.rotation + 180,
                    scale: 0.5,
                  }}
                  transition={{
                    duration: 1.5,
                    type: "spring",
                    stiffness: 60,
                    damping: 15,
                    delay: index * 0.1,
                  }}
                  whileHover={{
                    scale: config.scale * 1.1,
                    rotateY: 0,
                    z: 100,
                    y: config.translateY - 20,
                    transition: { 
                      duration: 0.3,
                      type: "spring",
                      stiffness: 300,
                      damping: 20
                    }
                  }}
                  style={{
                    zIndex: isCenter ? 10 : 5 - Math.abs(index - 2),
                  }}
                >
                  <motion.div
                    className="hero__card-inner"
                    animate={{
                      y: [0, -10, 0] // Always animate, no pause condition
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.2,
                      type: "tween"
                    }}
                  >
                    <motion.img
                      src={card.image}
                      alt="Cybersecurity card"
                      className="hero__card-image"
                      whileHover={{ 
                        boxShadow: "0 25px 50px rgba(255, 69, 0, 0.4)",
                        filter: "brightness(1.1)",
                      }}
                      layoutId={`card-${card.id}`}
                    />
                    <motion.div 
                      className="hero__card-glow"
                      animate={{ 
                        opacity: isCenter ? [0.3, 0.6, 0.3] : 0,
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        type: "tween"
                      }}
                    />
                  </motion.div>

                  {/* Add subtle rotation animation */}
                  <motion.div
                    className="hero__card-shimmer"
                    animate={{
                      background: [
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 60%, transparent 100%)",
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 60%, transparent 100%)"
                      ],
                      x: ["-100%", "100%"]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                      repeatDelay: 3
                    }}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Particle effects for magical feel */}
          <div className="hero__particles">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="hero__particle"
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  x: [(i % 2 === 0 ? 50 : -50), (i % 2 === 0 ? 100 : -100)],
                  y: [0, -100]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: "easeOut",
                  type: "tween"
                }}
              />
            ))}
          </div>
        </div>

        {/* Shuffle indicator */}
        <div className="hero__shuffle-indicator">
          {shuffleConfigurations.map((_, index) => (
            <motion.span
              key={index}
              className="hero__shuffle-dot"
              animate={{
                scale: index === shuffleIndex ? 1.5 : 1,
                backgroundColor: index === shuffleIndex ? '#FF4500' : '#E5E7EB'
              }}
              transition={{ duration: 0.3 }}
            />
          ))}
        </div>
        
        <motion.p 
          className="hero__description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          An exciting, fast-paced cybersecurity card game designed to help individuals, teams, and organizations strengthen their cyber awareness while having fun. Build your defenses, outsmart cyber threats, and become the ultimate Cyber Defender!
        </motion.p>
        
        <motion.div 
          className="hero__buttons"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <motion.a 
            href="#get-the-game" 
            className="hero__button hero__button--primary"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(255, 69, 0, 0.4)" }}
            whileTap={{ scale: 0.95 }}
          >
            Get The Game
          </motion.a>
          <motion.a 
            href="#about" 
            className="hero__button hero__button--secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Learn More
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;